import Link from '@/routing/Link'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { ImportActionBarProps } from '@/types/components/audiences/import-shell'

/**
 * Consistent bottom action bar for import wizard steps.
 *
 * On mobile the bar is sticky to the bottom of the viewport with a gradient
 * fade above it and safe-area inset padding. Buttons stretch full-width on
 * mobile and revert to auto width on sm+.
 *
 * On desktop the legacy mt-auto centred layout is preserved.
 */
export function ImportActionBar({
  children,
  backLabel,
  backHref,
  onBack,
  backDisabled,
  backVariant = 'primary',
  nextLabel,
  onNext,
  nextDisabled,
  nextLoading,
  nextLoadingLabel,
  className,
}: ImportActionBarProps) {
  
  const barClass = cn(
    // Mobile: sticky to the bottom of the scroll container + safe-area padding
    'sticky bottom-0 z-10',
    // Gradient fade so content scrolls under the bar cleanly on mobile
    'bg-gradient-to-t from-background from-60% via-background/90 to-transparent',
    // Desktop: revert to the legacy inline layout
    'md:relative md:bottom-auto md:mt-auto md:bg-transparent',
    // Layout
    'flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3',
    'px-4 sm:px-0 pt-6',
    'pb-[max(20px,env(safe-area-inset-bottom,20px))] md:pb-[42px]',
    className,
  )

  if (children) {
    return <div className={barClass}>{children}</div>
  }

  return (
    <div className={barClass}>
      {backLabel ? (
        backHref ? (
          <Button
            variant="primary"
            size="lg"
            className="rounded-lg text-[16px] w-full sm:w-auto"
            asChild
            disabled={backDisabled}
          >
            <Link href={backHref}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {backLabel}
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="rounded-lg text-[16px] w-full sm:w-auto"
            onClick={onBack}
            disabled={backDisabled}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {backLabel}
          </Button>
        )
      ) : null}

      {nextLabel ? (
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="rounded-lg text-[16px] w-full sm:w-auto"
          onClick={onNext}
          disabled={nextDisabled || nextLoading}
        >
          {nextLoading ? (
            <>
              {nextLoadingLabel ?? nextLabel}
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            </>
          ) : (
            <>
              {nextLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      ) : null}
    </div>
  )
}
